import 'login/components/LoginBanner/LoginBanner.scss';

import React from 'react';
import PropTypes from 'prop-types';

import { LocaleService, UrlService, WindowProxy } from '@sm360/widget-commons-core-services';
import {
    EVENTS as EVENTS_COMMONS_GENERAL,
    ModalService,
    AccountService,
    AnalyticsService,
    DATA_LAYER_VARIABLES as DATA_LAYER_VARS,
} from '@sm360/widget-commons-general';
import { ShowroomService } from '@sm360/widget-commons-vehicle';
import { isFunction } from 'lodash';

import TransactionSummary from 'login/components/Transaction/TransactionSummary/TransactionSummary.jsx';

import MessageBanner from 'login/components/Banners/MessageBanner.jsx';
import LoginMenu from 'login/components/LoginMenu/LoginMenu.jsx';
import SignUpModal from 'login/components/SignUp/SignUpModal.jsx';
import SignInModal from 'login/components/SignIn/SignInModal.jsx';
import ForgetPasswordModal from 'login/components/ForgetPassword/ForgetPasswordModal.jsx';
import ResetPasswordModal from 'login/components/ResetPassword/ResetPasswordModal.jsx';
import SaveConfirmationModal from 'login/components/SaveConfirmation/SaveConfirmationModal.jsx';

import EVENTS from 'login/constants/events.js';
import URL_PARAMS from 'login/constants/urlParams.js';
import { SIGN_UP, LOGIN, RESET_PASSWORD } from 'login/constants/modalNames.js';
import TAGS from 'login/constants/trackingTags';

class LoginBanner extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isAuthenticated: false,
            vehicleTradein: {
                tradeId: '',
                make: '',
                model: '',
                modelTrim: '',
                year: '',
                odometer: '',
                retail: '',
                amount: 0,
            },
        };

        this.onRegister = this.onRegister.bind(this);
        this.onLogin = this.onLogin.bind(this);
        this.onAuthentication = this.onAuthentication.bind(this);
        this.getVehicleTradeIn = this.getVehicleTradeIn.bind(this);
    }

    componentDidMount() {
        const $window = WindowProxy.get();

        if ($window) {
            $window.addEventListener(EVENTS.AUTHENTICATED, this.onAuthentication);
            $window.addEventListener(EVENTS.SHOW_LOGIN_MODAL, ModalService.get(LOGIN).open);
            $window.addEventListener(EVENTS.SHOW_REGISTER_MODAL, ModalService.get(SIGN_UP).open);
            $window.document.addEventListener(EVENTS_COMMONS_GENERAL.TRADEIN_CREATED, this.getVehicleTradeIn);
        }
        const resetCode = UrlService.getUrlParameter(URL_PARAMS.RESET_CODE);

        if (resetCode) {
            ModalService.get(RESET_PASSWORD).open();
        }

        const isAuthenticated = AccountService.isAuthenticated();
        if (isAuthenticated) {
            // eslint-disable-next-line react/no-did-mount-set-state
            this.setState({ isAuthenticated: true }, this.getVehicleTradeIn);
            AnalyticsService.setDataLayerVariable(DATA_LAYER_VARS.USER_TYPE, true);
        }
    }

    componentWillUnmount() {
        const $window = WindowProxy.get();

        if ($window) {
            $window.removeEventListener(EVENTS.AUTHENTICATED, this.onAuthentication);
            $window.removeEventListener(EVENTS.SHOW_LOGIN_MODAL, ModalService.get(LOGIN).open);
            $window.removeEventListener(EVENTS.SHOW_REGISTER_MODAL, ModalService.get(SIGN_UP).open);
            $window.document.removeEventListener(EVENTS_COMMONS_GENERAL.TRADEIN_CREATED, this.getVehicleTradeIn);
        }
    }

    onAuthentication() {
        this.setState({ isAuthenticated: AccountService.isAuthenticated() }, this.getVehicleTradeIn);
        AnalyticsService.setDataLayerVariable(DATA_LAYER_VARS.USER_TYPE, true);
    }

    onRegister() {
        AnalyticsService.gaTrigger(TAGS.LOGIN_WIDGET__REGISTER());
        AnalyticsService.gaTrigger(TAGS.MY_TRANSACTION__CREATE_AN_ACCOUNT(WindowProxy.location().pathname));

        const modal = ModalService.get(SIGN_UP);
        modal.open({ context: 'widget.login.signup.information' });
    }

    onLogin(onClose) {
        AnalyticsService.gaTrigger(TAGS.LOGIN_WIDGET__LOGIN());
        AnalyticsService.gaTrigger(TAGS.MY_TRANSACTION__LOGIN(WindowProxy.location().pathname));
        const modal = ModalService.get(LOGIN);
        modal.addCallbacks({
            onClose: () => {
                if (isFunction(onClose)) {
                    onClose();
                }
                return true;
            },
        });
        modal.open({ context: 'widget.login.signin.information' });
    }

    getVehicleTradeIn() {
        if (this.state.isAuthenticated) {
            ShowroomService.loadTradeInVehicle().then((tradeIn) => {
                if (tradeIn && tradeIn.vehicle) {
                    this.setState({
                        vehicleTradein: {
                            tradeId: tradeIn.id,
                            make: tradeIn.vehicle.make,
                            model: tradeIn.vehicle.model,
                            modelTrim: tradeIn.vehicle.modelTrim,
                            year: tradeIn.vehicle.year,
                            odometer: tradeIn.vehicle.odometer,
                            retail: tradeIn.vehicle.retail,
                            amount: tradeIn.amount,
                        },
                    });
                }
            });
        }
    }

    render() {
        const { isNewEnabled, isUsedEnabled, usedUrl } = this.props;
        const buttonView = (
            <div className="login-content">
                <button
                    id="button-login"
                    className="btn-login button secondary"
                    onClick={this.onLogin}
                    data-test="login.sign-in.open"
                >
                    <icon className="icon-login" />
                    <span className="label">{LocaleService.t('LOGIN.LOGIN_BANNER.LOGIN')}</span>
                </button>
                <button
                    id="button-register"
                    className="btn-register button sr-button-1 is-small"
                    onClick={this.onRegister}
                    data-test="login.sign-up.open"
                >
                    <span className="label">{LocaleService.t('LOGIN.LOGIN_BANNER.GET_STARTED')}</span>
                </button>
            </div>
        );

        const menuView = (
            <div className="login-content login-authenticated ">
                <LoginMenu />
            </div>
        );
        return (
            <div
                id="LoginBanner"
                className="LoginBanner widget-login-desktop widget-login-desktop-reset sr-topbar-login"
            >
                <div className="LoginBannerWrapper clearfix">
                    <MessageBanner text={this.props.messageBanner.text} level={this.props.messageBanner.level} />

                    <TransactionSummary
                        isNewEnabled={isNewEnabled}
                        isUsedEnabled={isUsedEnabled}
                        usedUrl={usedUrl}
                        vehicleTradein={this.state.vehicleTradein}
                        isAuthenticated={this.state.isAuthenticated}
                        loginClick={this.onLogin}
                    />

                    {this.state.isAuthenticated ? menuView : buttonView}

                    <SignUpModal />
                    <SignInModal />
                    <ForgetPasswordModal />
                    <ResetPasswordModal />
                    <SaveConfirmationModal />
                </div>
            </div>
        );
    }
}

LoginBanner.defaultProps = {
    isNewEnabled: true,
    isUsedEnabled: true,
    usedUrl: '',
};

LoginBanner.propTypes = {
    messageBanner: PropTypes.shape({
        text: PropTypes.string.isRequired,
        level: PropTypes.string.isRequired,
    }).isRequired,
    isNewEnabled: PropTypes.bool,
    isUsedEnabled: PropTypes.bool,
    usedUrl: PropTypes.string,
};

LoginBanner.displayName = 'LoginBanner';

export default LoginBanner;



// WEBPACK FOOTER //
// ./src/components/LoginBanner/LoginBanner.jsx